import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';

import { ScrollableContentBody, ScrollableContentHeader, ScrollableContentRoot } from './ScrollableContent.style';

type OverflowObservers = {
  resize?: ResizeObserver;
  mutation?: MutationObserver;
};

export type ScrollableContentProps = {
  header: ReactNode;
  children: ReactNode;
};

export function ScrollableContent({ header, children }: ScrollableContentProps) {
  const [hasOverflow, setHasOverflow] = useState(false);
  const observersRef = useRef<OverflowObservers>({});

  const contentRef = useCallback((element: HTMLDivElement | null) => {
    observersRef.current.resize?.disconnect();
    observersRef.current.mutation?.disconnect();
    observersRef.current = {};

    if (!element) {
      setHasOverflow(false);
      return;
    }

    const contentElement = element;

    function updateOverflow() {
      setHasOverflow(contentElement.scrollHeight > contentElement.clientHeight);
    }

    updateOverflow();

    if (typeof ResizeObserver !== 'undefined') {
      const resize = new ResizeObserver(updateOverflow);
      resize.observe(contentElement);
      observersRef.current.resize = resize;
    }

    if (typeof MutationObserver !== 'undefined') {
      const mutation = new MutationObserver(updateOverflow);
      mutation.observe(contentElement, { childList: true, subtree: true });
      observersRef.current.mutation = mutation;
    }
  }, []);

  return (
    <ScrollableContentRoot>
      <ScrollableContentHeader hasOverflow={hasOverflow}>{header}</ScrollableContentHeader>
      <ScrollableContentBody ref={contentRef}>{children}</ScrollableContentBody>
    </ScrollableContentRoot>
  );
}
